/**
 * Search Result Cache
 * Caches search results to reduce API calls and provide instant results
 *
 * Features:
 * - Time-based expiration (default 60 seconds)
 * - Automatic cleanup of old entries
 * - Normalized keys for consistent caching
 * - Size limits to prevent memory issues
 */

(function() {
  'use strict';

  class SearchCache {
    /**
     * @param {number} ttl - Time to live in milliseconds (default: 60000 = 1 minute)
     * @param {number} maxSize - Maximum number of cached entries (default: 50)
     */
    constructor(ttl = 60000, maxSize = 50) {
      this.cache = new Map();
      this.ttl = ttl;
      this.maxSize = maxSize;
      this.stats = {
        hits: 0,
        misses: 0,
        sets: 0
      };
    }

    /**
     * Generate a normalized cache key
     * @param {string} query - The search query
     * @param {Object} options - Additional search options (filters, etc.)
     * @returns {string} The cache key
     */
    _generateKey(query, options = {}) {
      // Normalize query: lowercase, trim, remove extra spaces
      const normalizedQuery = (query || '').toLowerCase().trim().replace(/\s+/g, ' ');

      // Create a stable key including options
      const optionsKey = Object.keys(options)
        .sort()
        .map(key => `${key}:${options[key]}`)
        .join('|');

      return optionsKey ? `${normalizedQuery}::${optionsKey}` : normalizedQuery;
    }

    /**
     * Get a cached result
     * @param {string} query - The search query
     * @param {Object} options - Additional search options
     * @returns {any|null} The cached result or null if not found/expired
     */
    get(query, options = {}) {
      const key = this._generateKey(query, options);
      const item = this.cache.get(key);

      if (!item) {
        this.stats.misses++;
        return null;
      }

      // Check if expired
      if (Date.now() > item.expiry) {
        this.cache.delete(key);
        this.stats.misses++;
        return null;
      }

      this.stats.hits++;
      return item.value;
    }

    /**
     * Set a cached result
     * @param {string} query - The search query
     * @param {any} value - The result to cache
     * @param {Object} options - Additional search options
     */
    set(query, options = {}, value) {
      // Don't cache empty queries
      if (!query || !query.trim()) {
        return;
      }

      const key = this._generateKey(query, options);

      // Implement LRU: Remove oldest entry if at max size
      if (this.cache.size >= this.maxSize) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }

      this.cache.set(key, {
        value,
        expiry: Date.now() + this.ttl,
        createdAt: Date.now()
      });

      this.stats.sets++;
    }

    /**
     * Check if a query is cached and not expired
     * @param {string} query - The search query
     * @param {Object} options - Additional search options
     * @returns {boolean}
     */
    has(query, options = {}) {
      const key = this._generateKey(query, options);
      const item = this.cache.get(key);

      if (!item) return false;

      if (Date.now() > item.expiry) {
        this.cache.delete(key);
        return false;
      }

      return true;
    }

    /**
     * Clear all cached results
     */
    clear() {
      this.cache.clear();
      this.stats = {
        hits: 0,
        misses: 0,
        sets: 0
      };
    }

    /**
     * Clean up expired entries
     */
    cleanup() {
      const now = Date.now();
      for (const [key, item] of this.cache.entries()) {
        if (now > item.expiry) {
          this.cache.delete(key);
        }
      }
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache statistics
     */
    getStats() {
      const total = this.stats.hits + this.stats.misses;
      const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(1) : 0;

      return {
        hits: this.stats.hits,
        misses: this.stats.misses,
        sets: this.stats.sets,
        hitRate: `${hitRate}%`,
        size: this.cache.size,
        maxSize: this.maxSize
      };
    }

    /**
     * Log cache statistics to console
     */
    logStats() {
      const stats = this.getStats();
      console.log('🔍 Search Cache Statistics:', {
        'Hit Rate': stats.hitRate,
        'Cache Hits': stats.hits,
        'Cache Misses': stats.misses,
        'Total Sets': stats.sets,
        'Current Size': `${stats.size}/${stats.maxSize}`
      });
    }
  }

  // Create global search cache instance
  // TTL: 60 seconds (1 minute)
  // Max Size: 50 entries
  window.searchCache = new SearchCache(60000, 50);

  // Auto-cleanup every 5 minutes
  setInterval(function() {
    window.searchCache.cleanup();
  }, 5 * 60 * 1000);

  // Optional: Log stats every 2 minutes (only in development)
  if (window.location.hostname === 'localhost' || window.location.search.includes('debug')) {
    setInterval(function() {
      if (window.searchCache.getStats().sets > 0) {
        window.searchCache.logStats();
      }
    }, 2 * 60 * 1000);
  }

  // Expose method to manually check cache stats
  window.getSearchCacheStats = function() {
    window.searchCache.logStats();
    return window.searchCache.getStats();
  };

  console.log('✅ Search cache initialized (60s TTL, 50 max entries)');

})();
